import * as TagPrisonUtil from "../utils/TagPrisonUtil";
import { rememberKeysBy } from "../utils/FunctionUtil";
import GdprStorage from "../storage/GdprStorage";
import { createGtmEvent } from "../utils/GTM";
import { enrichEvent } from "../tracking/WebAnalyticsTrackingUtil";
import { toGa4Structure } from "./Ga4PayloadConverter";
// list of events which are processed by this consumer
const EVENTS_TO_CONSUME = [
	"page",
	"stateUpdate",
	"basket",
	"lists",
	"article",
	"search",
	"user",
	"click",
	"order",
	"login_success",
	"checkout.page",
	"shoppingcart", //only putting 'cart' allows sending too many events to GA, so putting the full event name for RemoveFromCart GA ecommomerce enhancement tracking
	"cart.item.delete",
	"cart.items.delete",
	"manual",
];
// whitelist of hidden data fields for each event type
const hiddenFieldsWhiteList = {
	stateUpdate: [
		"articles",
		"submitId",
		"orderAmount",
		"orderTax",
		"shippingCosts",
		"cid",
		"icid",
		"totalArticleValue",
	],
	"article.adwords": ["articles"],
	"article.productView": ["ecommerce", "saleCycle"],
	"article.recommendationView": ["ecommerce"],
	"article.recommendationClick": ["event", "ecommerce", "eventCallback"],
	"checkout.page.delivery": ["event", "ecommerce"],
	"checkout.page.delivery_reworked": ["event", "ecommerce"],
	"checkout.page.address": ["event", "ecommerce"],
	"checkout.page.payment": ["event", "ecommerce"],
	"checkout.page.summary": ["event", "ecommerce"],
	"checkout.page.summary_reworked": ["event", "ecommerce"],
	"checkout.page.confirmation": ["event", "ecommerce"],
	"basket.add": ["event", "ecommerce", "saleCycle"],
	"basket.remove": ["event", "ecommerce", "saleCycle"],
	"basket.change": ["event", "ecommerce"],
	"cart.item.delete": ["event", "ecommerce"],
	"cart.items.delete": ["event", "ecommerce"],
	"lists.add": ["event", "ecommerce", "eventAction", "eventLabel"],
	"order.submit.success.ga360": ["event", "ecommerce"],
};
const EVENT_DRAIN_OVERFLOW_TIMEOUT_MS = 10;
const MAX_QUEUED_EVENTS = 100;
// reference to the iframe
let iframe: HTMLIFrameElement | null | undefined;
let eventQueue: Array<Record<string, any>> = [];
let processQueue = false;

// sets the reference to the iframe
function setIframeRef(iframeRef: HTMLIFrameElement | null) {
	iframe = iframeRef;
}

function send2IFrame(event) {
	if (eventQueue.length < MAX_QUEUED_EVENTS) {
		eventQueue.push(event);

		if (processQueue) {
			processEventQueue();
		}
	} else {
		logQueueFullOnce();
	}
}

//This is done so that non-mandatory tags are reset properly, for example:
//    {event:"FOO", x:4, y:5})  ->  {event:"FOO", x:4, y:5}
//    {event:"FOO", x:7, z:4})  ->  {event:"FOO", x:7, y:null, z:4}
//    {event:"BAR",x:5})        ->  {event:"BAR", x:5}
const pushToDataLayer: (...args: Array<any>) => any = rememberKeysBy(
	event => {
		if (window.hasGA4) {
			if (!window.dataLayer) {
				window.dataLayer = [];
			}

			window.dataLayer.push(event);
		}
	},
	event => event["event"],
);

const pushToTracking = (event: any, flatData: Record<string, any>) => {
	send2IFrame(event);
	const enriched = enrichEvent(event, flatData);
	pushToDataLayer(enriched);
};

function sendToGTM(eventType, data, hiddenData) {
	// The tags object (if it is one) contains the information that is specific to each
	// structured tracking call but useful for most of the tracking frameworks.
	const { tags, ...rest } = data;
	const flatData = { ...tags, ...rest };

	if (!(flatData && eventType)) {
		return;
	}

	// There is more than 1 GTM, some are inside
	// the iFrame others are directly on the page
	const event = createGtmEvent(eventType, flatData, hiddenData);

	if (!event) {
		return;
	}

	pushToTracking(event, flatData);
	// translating and pushing a GA4 event
	const ga4EventPayload = toGa4Structure(eventType, hiddenData, tags);

	if (ga4EventPayload) {
		const ga4Event = createGtmEvent(eventType, flatData, ga4EventPayload);
		pushToTracking(ga4Event, flatData);
	}
}

const logQueueFullOnce = (() => {
	let isLogQueueFullOnceExecuted = false;
	return () => {
		if (!isLogQueueFullOnceExecuted) {
			// we do tracking on console.error, so we want to switch the flag early to avoid infinite loop
			isLogQueueFullOnceExecuted = true;
			console.error(`More than ${MAX_QUEUED_EVENTS} detected. Some events WILL be skipped :(`);
		}
	};
})();

function processEventQueue() {
	if (eventQueue.length === 0) {
		return;
	}

	const oldestEvent = eventQueue.shift();

	if (iframe?.contentWindow) {
		iframe.contentWindow.postMessage(oldestEvent, "*");
	}

	setTimeout(() => processEventQueue(), EVENT_DRAIN_OVERFLOW_TIMEOUT_MS);
}

function filterHiddenFields(
	whiteList: Array<string> | null | undefined,
	hiddenFields: object | null | undefined,
): Record<string, any> {
	if (hiddenFields === null || hiddenFields === undefined) {
		return {};
	}

	return Object.fromEntries(Object.entries(hiddenFields).filter(([key]) => whiteList?.includes(key)));
}

// so far, we do not send data generated by employees.
function sendingToGTMIsAllowed(eventType: string) {
	let shouldSend = false;
	const gdprAllowed =
		GdprStorage.isCategoryAllowed(GdprStorage.PROMOTIONAL, "tagprison", "sedingToGTMIsAllowed") === true;

	if (!gdprAllowed) {
		return false;
	}

	EVENTS_TO_CONSUME.forEach(allowedPrefix => {
		if (eventType.startsWith(allowedPrefix)) {
			shouldSend = true;
		}
	});
	return shouldSend;
}

function trackStructured(
	eventType: string,
	structuredData: any,
	hiddenData: object | null | undefined,
	dispatcherData: any,
) {
	const dataForGTM = { ...structuredData, ...dispatcherData.sessionStoreInfo, ...dispatcherData.userInfo };
	track(eventType, dataForGTM, hiddenData);
}

function track(eventType: string, dataForGTM: any, hiddenData: object | null | undefined) {
	if (sendingToGTMIsAllowed(eventType)) {
		const whiteList = hiddenFieldsWhiteList[eventType];
		const filteredHiddenData = filterHiddenFields(whiteList, hiddenData);
		sendToGTM(eventType, dataForGTM, filteredHiddenData);
	}
}

window.addEventListener("message", receiveMessage, false);

function removeProtocol(uri: string): string {
	return uri.replace(/^(https?:|)\/\//, "");
}

function isEventOriginExpected(eventOrigin: string): boolean {
	const expectedDomain = TagPrisonUtil.getIFrameDomain(window);
	const originWithoutProtocoll = removeProtocol(eventOrigin);
	const contains = originWithoutProtocoll.length > 0 && expectedDomain.indexOf(originWithoutProtocoll) >= 0;
	return contains;
}

function receiveMessage(event: Record<string, any>) {
	if (!isEventOriginExpected(event.origin)) {
		return;
	}

	if (event.data && event.data.type === "TAG_PRISON_FRAME_READY") {
		processQueue = true;
		processEventQueue();
	}
}

function resetQueue() {
	eventQueue = [];
	processQueue = false;
}

export default {
	track,
	trackStructured,
	setIframeRef,
	pushToDataLayer,
	pushToTracking,
	forTestingOnly: {
		filterHiddenFields,
		isEventOriginExpected,
		resetQueue,
		receiveMessage,
	},
};
